import React from 'react';

import Typography from '@mui/material/Typography';

import HandsonArticleLayout from './components/HandsonArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowChecklist from './components/WorkflowChecklist';
import WorkflowDirectoryTree from './components/WorkflowDirectoryTree';
import WorkflowGoalBox from './components/WorkflowGoalBox';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLinkCard from './components/WorkflowLinkCard';
import WorkflowList from './components/WorkflowList';
import WorkflowLockedCodeBlock from './components/WorkflowLockedCodeBlock';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const seriesSteps = [
  {
    label: 'Steeringルール',
    title: 'Steeringルールの書き方を学ぶ',
    path: '/devdocs/handson/steering-rules',
  },
  { label: 'rfp作成', title: 'rfpを作成する', path: '/devdocs/handson/rfp' },
  {
    label: 'proposal/business',
    title: 'proposal/businessを生成する',
    path: '/devdocs/handson/proposal-business',
  },
  { label: 'usecase', title: 'usecaseを生成する', path: '/devdocs/handson/usecase' },
  { label: 'product', title: 'productの機能一覧を生成する', active: true },
];

const HandsonProduct: React.FC = () => {
  return (
    <HandsonArticleLayout
      title="要件定義編 — product作成"
      seriesName="要件定義編"
      tags={['Hands-on', 'Product', 'Requirements Complete']}
      seriesSteps={seriesSteps}
      prevLink={{ label: 'usecase生成', path: '/devdocs/handson/usecase' }}
      nextLink={{ label: 'structure/techルールの作成', path: '/devdocs/handson/structure-tech' }}
    >
      <WorkflowGoalBox
        prerequisites={['「要件定義編 — usecase生成」が完了している']}
        completionCriteria={[
          '.kiro/steering/create_product.md が作成されている',
          '.kiro/steering/product.md の機能一覧セクションがAI生成により埋まっている',
          'セクション4のチェックリスト全項目に合格している',
          '少なくとも1回はルール改善→再生成のサイクルを経験している',
        ]}
      >
        create_product.mdのSteeringルールを作成し、AIでproduct.mdの機能一覧を生成して要件定義フェーズを完了する。
      </WorkflowGoalBox>

      <WorkflowLinkCard
        title="要件定義編 — usecase生成"
        description="前の記事"
        path="/devdocs/handson/usecase"
      />

      <WorkflowLinkCard
        title="要件定義編 — Steeringルールの書き方"
        description="product.md初版作成"
        path="/devdocs/handson/steering-rules"
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'この章で体験すること' },
          { id: 'section-2', label: 'Steeringルールを作成する' },
          { id: 'section-3', label: 'AIでproduct.mdの機能一覧を生成する' },
          { id: 'section-4', label: 'チェックリストで品質を確認する' },
          { id: 'section-5', label: 'ルールを改善して再生成する' },
          { id: 'section-6', label: '要件定義フェーズの振り返り' },
          { id: 'section-7', label: '現在のプロジェクト構成' },
        ]}
      />

      {/* セクション1 */}
      <WorkflowSection title="この章で体験すること" id="section-1" number={1}>
        <WorkflowText>
          「要件定義編 —
          Steeringルールの書き方」でproduct.mdの骨格（概要・ビジネスルール・ユーザーストーリー・非機能要件）を作成しました。あのとき「機能一覧」は「後続ステップで追記する」と空にしていました。
        </WorkflowText>

        <WorkflowLinkCard
          title="要件定義編 — Steeringルールの書き方"
          description="product.md初版作成"
          path="/devdocs/handson/steering-rules"
        />

        <WorkflowText>
          usecases.mdまでの全成果物が揃った今、AIにproposal/business/usecasesの内容を読み取らせて機能一覧を自動生成させます。
        </WorkflowText>

        <WorkflowInfoBox type="info">
          <Typography variant="body2" fontWeight={600}>
            この章は要件定義フェーズの総まとめです。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            「要件定義編 — proposal/business生成」「要件定義編 —
            usecase生成」と同じPDCAサイクルを使いますが、作業量は少なめです。生成対象がproduct.md内の1セクション（機能一覧）だけなので、シンプルに終わります。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="この章の流れ" subNumber="1.1">
          <WorkflowList
            variant="ordered"
            items={[
              'Steeringルールを作成する（create_product.md）',
              'AIでproduct.mdの機能一覧を生成する',
              'チェックリストで品質を確認する',
              'ルールを改善して再生成する（必要に応じて）',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション2 */}
      <WorkflowSection title="Steeringルールを作成する" id="section-2" number={2}>
        <WorkflowSection title="何を作るか" subNumber="2.1">
          <WorkflowTable
            headers={['ファイル', '役割']}
            rows={[
              [
                '.kiro/steering/create_product.md',
                'product.md の機能一覧生成ルール（入力ファイル指定・テンプレート・記載ルール・チェックリスト）',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="ルールに書くべき情報" subNumber="2.2">
          <WorkflowTipBox title="なぜシンプルでよいのか">
            <WorkflowTable
              headers={['観点', 'create_proposal / create_usecases', 'create_product']}
              rows={[
                ['生成範囲', 'ファイル全体を新規生成', '既存ファイルの1セクションだけ追記'],
                [
                  '入力の複雑さ',
                  '対話で仕様を決める / 2ファイルから変換',
                  '確定済み成果物から機能名と概要を抽出するだけ',
                ],
                [
                  '判断の余地',
                  'エラーケース網羅・DB操作レベルの結果記述など高度',
                  'proposal.mdのAPI一覧をそのまま機能一覧に転記する程度',
                ],
              ]}
            />
          </WorkflowTipBox>

          <WorkflowTable
            headers={['順番', 'セクション', '書くこと']}
            rows={[
              ['1', '目的', 'このルールが何のために存在するか'],
              ['2', '入力ファイル', 'どのファイルを読んで機能一覧を作るか'],
              ['3', '生成対象', 'product.mdのどのセクションを埋めるか'],
              ['4', 'テンプレート', '機能一覧のフォーマット'],
              ['5', '記載ルール', '各項目をどう埋めるか'],
              ['6', 'チェックリスト', '生成後の品質確認項目'],
              ['7', '禁止事項', 'AIがやってはいけないこと'],
            ]}
          />

          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>読み込みモードの設定</strong>: このファイルは <code>manual</code>{' '}
              モードにしてください。product.md
              はalways（常時読み込み）のSteeringファイルなので、fileMatchにすると毎回生成ルールが読み込まれてしまいます。生成時だけ{' '}
              <code>#create_product</code> で明示的に呼び出します。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="自分で書いてみる" subNumber="2.3">
          <WorkflowText>以下のファイルを作成してください。</WorkflowText>
          <WorkflowList items={['.kiro/steering/create_product.md']} />
          <WorkflowInfoBox type="warning">
            <Typography variant="body2">
              <strong>注意</strong>: このルールは他の3つ（create_proposal / create_business /
              create_usecases）と比べて非常にシンプルです。機能一覧は「proposal.mdのAPI一覧を人間向けに言い換えたもの」が基本形なので、過度に複雑なルールは不要です。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_product.md - 完成例" subNumber="2.4">
          <WorkflowLockedCodeBlock password="Wq5vLn">
            {`---
inclusion: manual
---

# product.md 機能一覧生成ルール

## 目的

proposal.md / business.md / usecases.md の全成果物を元に、
product.md の「機能一覧」セクションを生成するためのルール。

## 入力ファイル

以下のファイルを読み込んで機能一覧を生成する。

| ファイル | 主に参照する情報 |
|----------|------------------|
| \`*.proposal.md\` | API一覧（API ID・API名・Endpoint） |
| \`*.business.md\` | BUCの目的（ユーザー視点の操作概要） |
| \`*.usecases.md\` | UC一覧（確定済みのユースケース全量） |

## 生成対象

- product.md 内の \`## 機能一覧\` セクションを埋める。
- 他のセクション（概要・ビジネスルール・ユーザーストーリー・非機能要件）は変更しない。

## テンプレート構造

## 機能一覧

| 機能 | 概要 |
|------|------|
| {機能名} | {この機能が何をするか1行で} |

## 記載ルール

### 機能名

- proposal.md のAPI名をそのまま使う、または複数APIをまとめた機能名にする。

### 概要

- ユーザー目線で「何ができるか」を1行で書く。
- 技術用語（API, DB, Entity）は使わない。

### 並び順

- proposal.md のAPI ID順に並べる。

## チェックリスト

- [ ] proposal.md の全APIが機能一覧のいずれかの行でカバーされているか
- [ ] 概要がユーザー目線で書かれているか（技術用語を使っていないか）
- [ ] 機能名が重複していないか
- [ ] product.md の他のセクションを変更していないか

## 禁止事項

- proposal.md に存在しないAPIに対応する機能を追加しない。
- 概要に技術的な実装詳細を書かない。
- product.md の機能一覧以外のセクションを変更しない。
- 機能名を独自に創作しない。`}
          </WorkflowLockedCodeBlock>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション3 */}
      <WorkflowSection title="AIでproduct.mdの機能一覧を生成する" id="section-3" number={3}>
        <WorkflowSection title="生成を指示する" subNumber="3.1">
          <WorkflowText>
            proposal.md / business.md / usecases.md
            をコンテキストに含めて、product.mdの機能一覧生成を指示してください。create_product.md
            はmanualモードなので、<code>#create_product</code> でルールを明示的に読み込ませます。
          </WorkflowText>

          <WorkflowChatBubble role="human">
            {`#create_product #todo.proposal.md #todo.business.md #todo.usecases.md を読み込んで、
product.md の「機能一覧」セクションを埋めてください。`}
          </WorkflowChatBubble>

          <WorkflowInfoBox type="info">
            <Typography variant="body2">
              manualモードのルールは <code>#ファイル名</code>{' '}
              で明示的に読み込ませます。create_proposal.md / create_business.md / create_usecases.md
              はfileMatchモードで自動読み込みでしたが、create_product.md
              はproduct.mdが常時読み込みファイルのためmanualにしています。生成時だけルールが必要なので、この方が適切です。
            </Typography>
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="生成結果を確認する" subNumber="3.2">
          <WorkflowText>
            product.mdの機能一覧が埋まったことを確認してください。他のセクション（概要・ビジネスルール・ユーザーストーリー・非機能要件）が変更されていないことも合わせて確認します。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4 */}
      <WorkflowSection title="チェックリストで品質を確認する" id="section-4" number={4}>
        <WorkflowSection title="チェック項目" subNumber="4.1">
          <WorkflowChecklist
            groups={[
              {
                title: 'チェックリスト',
                items: [
                  'proposal.md の全APIが機能一覧のいずれかの行でカバーされているか',
                  '概要がユーザー目線で書かれているか（技術用語を使っていないか）',
                  '機能名が重複していないか',
                  'product.md の他のセクション（概要・ビジネスルール等）が変更されていないか',
                  '機能一覧の並び順がproposal.mdのAPI ID順と整合しているか',
                ],
              },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="不合格パターンと対処" subNumber="4.2">
          <WorkflowTable
            headers={['分類', '対処']}
            rows={[
              ['[ルール不足] ルールの記載が甘くてAIが間違える', 'セクション5でルールを改善する'],
              ['[部分ミス] 生成物の一部が間違っている', 'AIに修正指示を出す'],
            ]}
          />

          <WorkflowText>以下はよくある不合格パターンです。</WorkflowText>

          <WorkflowTable
            headers={['#', '症状', '原因', '改善']}
            rows={[
              ['1', '概要に技術用語が混入', '記載ルールに具体例が不足', '記載ルールに変換例を追記'],
              [
                '2',
                'proposal.mdのAPIが漏れている',
                '一括生成時にAPIを見落とし',
                'AIに突き合わせて不足追加を指示',
              ],
              [
                '3',
                '他のセクションが書き換わっている',
                'AIが「改善」として変更した',
                '禁止事項をより強調する',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5 */}
      <WorkflowSection title="ルールを改善して再生成する" id="section-5" number={5}>
        <WorkflowInfoBox type="important">
          <Typography variant="body2" fontWeight={600}>
            重要: このセクションは完了条件の1つです。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            1回目の生成でチェックリストに全合格しても、意図的にルールを1箇所削って再生成し、品質が下がることを確認してください。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="改善の手順" subNumber="5.1">
          <WorkflowText>
            「要件定義編 —
            proposal/business生成」と同じ手順（原因特定→修正箇所決定→Steering修正→再生成→再チェック）を繰り返してください。
          </WorkflowText>

          <WorkflowLinkCard
            title="要件定義編 — proposal/business生成"
            description="ルール改善の手順"
            path="/devdocs/handson/proposal-business"
          />

          <WorkflowChatBubble role="human">
            {`create_product.md のルールを更新しました。
#create_product を読み込んで、product.md の機能一覧を再生成してください。`}
          </WorkflowChatBubble>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション6 */}
      <WorkflowSection title="要件定義フェーズの振り返り" id="section-6" number={6}>
        <WorkflowInfoBox type="success">
          <Typography variant="body2" fontWeight={600}>
            おめでとうございます！ 要件定義フェーズが完了しました。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            rfp → proposal → business → usecases → product の全成果物が揃いました。
          </Typography>
        </WorkflowInfoBox>

        <WorkflowSection title="成果物一覧" subNumber="6.1">
          <WorkflowTable
            headers={['成果物', 'ファイル', '作成した記事']}
            rows={[
              [
                'プロダクト要件定義',
                '.kiro/steering/product.md',
                'Steeringルールの書き方 + product作成',
              ],
              ['要件草案', '.kiro/requirements/todo/todo.rfp.md', 'rfp作成'],
              [
                '技術仕様決定事項',
                '.kiro/requirements/todo/todo.proposal.md',
                'proposal/business生成',
              ],
              [
                'ビジネスユースケース',
                '.kiro/requirements/usecases/todo.business.md',
                'proposal/business生成',
              ],
              [
                'システムユースケース',
                '.kiro/requirements/usecases/todo.usecases.md',
                'usecase生成',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="Steeringルール一覧" subNumber="6.2">
          <WorkflowTable
            headers={['ルール', 'ファイル', '作成した記事']}
            rows={[
              [
                'プロダクト要件定義（常時参照）',
                '.kiro/steering/product.md',
                'Steeringルールの書き方 + product作成',
              ],
              ['proposal生成ルール', '.kiro/steering/create_proposal.md', 'proposal/business生成'],
              ['business生成ルール', '.kiro/steering/create_business.md', 'proposal/business生成'],
              ['usecases生成ルール', '.kiro/steering/create_usecases.md', 'usecase生成'],
              ['product機能一覧生成ルール', '.kiro/steering/create_product.md', 'product作成'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="身についたスキル" subNumber="6.3">
          <WorkflowTable
            headers={['スキル', '内容']}
            rows={[
              [
                'Steeringルールの設計',
                '目的・テンプレート・記載ルール・禁止事項を構造化して書ける',
              ],
              ['PDCAサイクルの実践', 'ルール→生成→チェック→改善のサイクルで品質を安定させられる'],
              [
                'AIへの指示設計',
                '入力ファイルの指定・コンテキスト制御・修正指示の出し方を理解している',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7 */}
      <WorkflowSection title="現在のプロジェクト構成" id="section-7" number={7}>
        <WorkflowDirectoryTree
          root="team-todo/"
          entries={[
            {
              name: '.kiro',
              color: '#1565c0',
              children: [
                {
                  name: 'requirements',
                  children: [
                    {
                      name: 'todo',
                      children: [{ name: 'todo.rfp.md' }, { name: 'todo.proposal.md' }],
                    },
                    {
                      name: 'usecases',
                      children: [{ name: 'todo.business.md' }, { name: 'todo.usecases.md' }],
                    },
                  ],
                },
                {
                  name: 'steering',
                  children: [
                    { name: 'product.md', desc: '機能一覧を追記して完成', highlight: '#e65100' },
                    { name: 'create_proposal.md' },
                    { name: 'create_business.md' },
                    { name: 'create_usecases.md' },
                    { name: 'create_product.md', desc: '今回作成', highlight: '#e65100' },
                  ],
                },
              ],
            },
            { name: 'build.gradle' },
            { name: 'settings.gradle' },
            { name: 'docker-compose.yml' },
            { name: 'HELPME.md' },
            {
              name: 'src',
              children: [
                {
                  name: 'main',
                  children: [
                    {
                      name: 'java/com/example/teamtodo',
                      children: [
                        { name: 'TeamTodoApplication.java' },
                        { name: 'config', children: [{ name: 'SecurityConfig.java' }] },
                      ],
                    },
                    { name: 'resources', children: [{ name: 'application.yml' }] },
                  ],
                },
                { name: 'test', children: [] },
              ],
            },
          ]}
        />
      </WorkflowSection>

      {/* 次のステップ */}
      <WorkflowInfoBox type="preview">
        <Typography variant="body2" fontWeight={600}>
          要件定義編はここで完了です。お疲れ様でした！
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          次のステップ（実装編）では、ここで作成した成果物をインプットにして:
        </Typography>
        <WorkflowList
          items={[
            'structure.md / tech.md を作成する',
            'スキーマ定義・API仕様書を生成する',
            'バックエンドの設計・実装・テストをAI駆動で進める',
          ]}
        />
      </WorkflowInfoBox>

      <WorkflowLinkCard
        title="実装編 — structure/techルールの作成"
        description="実装フェーズの土台となるstructure.mdとtech.mdを自分で考えて作成する"
        path="/devdocs/handson/structure-tech"
      />

      <WorkflowChecklist
        groups={[
          {
            title: 'チェックリスト',
            items: [
              '.kiro/steering/create_product.md が作成されている',
              '.kiro/steering/product.md の機能一覧セクションがAI生成により埋まっている',
              'セクション4のチェックリスト全項目に合格している',
              '少なくとも1回はルール改善→再生成のサイクルを経験した',
            ],
          },
        ]}
      />
    </HandsonArticleLayout>
  );
};

export default HandsonProduct;
